import React, { useContext, useState, useEffect } from 'react';
import { Collapse, Row, Col, Alert, Label } from '@digitools/honest-react';
import axios from 'axios';
import { AuthenticationContext } from '../../../providers/AuthenticationProvider';
import { UnexpectedErrorContext } from '../../../providers/UnexpectedErrorProvider';
import { AccessLevelEnum } from '../types/permissionTypes';
import EmployerSubLocModal from '../modal/EmployerSubLocModal';
const EmployerSubsAndLocs = (props) => {
    const authContext = useContext(AuthenticationContext);
    const headerConfig = { headers: { Authorization: authContext.authHeader() } };
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const { setIsMessagingDisabled } = useContext(UnexpectedErrorContext);
    const [subLocs, setSubLocs] = useState([]);
    // TECH DEBT (follow-up): the access level is mirrored here as local state, kept in sync with the parent
    // via an effect down and onSubsLocsChange up, only because the parent mutates its `userInformation` object
    // directly rather than holding it in state, so changes don't flow back down reactively. This is derived
    // state that could be dropped once the parent owns it reactively. Initialize from the SAME prop we sync
    // from (props.subsidiariesAndLocations) — not props.userInfo!.subsidiariesAndLocations — so the two can't
    // silently diverge (they're equal at mount, and the sync effect would overwrite a userInfo value anyway).
    const [subAndLocAccessLevel, setSubAndLocAccessLevel] = useState(props.subsidiariesAndLocations);
    useEffect(() => setIsMessagingDisabled(false), []);
    // Mirror the access level the parent pushes down on a bulk Enable/Clear All (FULL / NO_ACCESS) so the
    // "All/None Selected" label updates immediately for BOTH actions. Previously this sync was gated on
    // props.clearAll, so only Clear All updated the label; Enable All left it stale until a page refresh.
    useEffect(() => {
        setSubAndLocAccessLevel(props.subsidiariesAndLocations);
        // A bulk Enable/Clear All lands the access level on FULL or NO_ACCESS. Re-sync the cached rows'
        // selection so a reopened modal reflects it immediately — previously the cache kept its stale
        // `selected` flags until a page refresh reset this component's state. Only the unambiguous
        // FULL/NO_ACCESS cases are touched; LIMITED (granular) selections are left untouched.
        if (props.subsidiariesAndLocations === AccessLevelEnum.FULL) {
            setSubLocs(prev => prev.map(data => ({ ...data, selected: true })));
        }
        else if (props.subsidiariesAndLocations === AccessLevelEnum.NO_ACCESS) {
            setSubLocs(prev => prev.map(data => ({ ...data, selected: false })));
        }
    }, [props.subsidiariesAndLocations]);
    const getSubsLocData = async () => {
        // Fetch once; on later opens reuse the loaded list (selection lives on these objects and persists
        // across opens). Returning the data lets the modal use it directly instead of reading it back off
        // props.subLocs in the same tick — that state hasn't committed yet on the first open, which left
        // the modal empty until a second open / refresh.
        if (subLocs.length > 0) {
            return subLocs;
        }
        const url = `/crs-service/er-admin/subs-locs/` + props.userInfo.userId + `?products=` + props.products;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: authContext.authHeader(),
                },
            });
            const rawData = response.data;
            const applySelection = (value) => rawData.map(data => ({ ...data, selected: value }));
            let subLocData = rawData; // LIMITED: keep the server's per-row flags as-is
            if (props.clearAll || subAndLocAccessLevel === AccessLevelEnum.NO_ACCESS) {
                subLocData = applySelection(false);
            }
            else if (subAndLocAccessLevel === AccessLevelEnum.FULL) {
                subLocData = applySelection(true);
            }
            setSubLocs(subLocData);
            return subLocData;
        }
        catch (error) {
            setError(true);
            setErrorMessage('Error while retrieving Subsidiaries/Locations');
            throw error;
        }
    };
    const saveSubsAndLocs = async (selectedIds, subsidiariesAndLocationsFromModal) => {
        try {
            setSubAndLocAccessLevel(subsidiariesAndLocationsFromModal);
            if (subsidiariesAndLocationsFromModal === AccessLevelEnum.LIMITED) {
                const crsServiceSaveRequestBody = {
                    portalUserNumber: props.userInfo.userId,
                    adminLocationIds: selectedIds,
                };
                await axios.put(`/crs-service/er-admin/subs-locs`, crsServiceSaveRequestBody, headerConfig);
            }
            if (subsidiariesAndLocationsFromModal === AccessLevelEnum.FULL ||
                subsidiariesAndLocationsFromModal === AccessLevelEnum.NO_ACCESS) {
                await axios.delete(`/crs-service/er-admin/subs-locs/` + props.userInfo.userId, headerConfig);
            }
            const userServiceRequestBody = {
                userId: props.userInfo.userId,
                subsidiariesAndLocations: subsidiariesAndLocationsFromModal,
            };
            await axios.patch(`/user-service/services/v1/users/employers`, userServiceRequestBody, headerConfig);
            // Keep the All Permissions Control in sync: a Subs/Locs change to anything other than FULL drops
            // the user out of the fully-maxed state (and returning to FULL can restore it).
            props.onSubsLocsChange?.(subsidiariesAndLocationsFromModal);
            setSuccess(true);
            setSuccessMessage('Subsidiaries/Locations saved successfully');
        }
        catch (error) {
            setError(true);
            setErrorMessage('Error while saving Subsidiaries/Locations');
            throw error;
        }
    };
    const closeAlert = () => {
        setError(false);
        setErrorMessage('');
        setSuccess(false);
        setSuccessMessage('');
    };
    const alert = success ? (<Alert id='success' type='success' closeable={true} onClose={closeAlert}>
      {successMessage}
    </Alert>) : error ? (<Alert data-testid='error' id='error' type='error' closeable={true} onClose={closeAlert}>
      {errorMessage}
    </Alert>) : (<></>);
    return (<div style={{ marginTop: '1.5rem' }}>
      <Collapse type='box' isOpen={true} title='Subsidiaries/Locations' data-testid={'sublocCollapse'}>
        {alert}
        <Row>
          <Col>
            <Label style={{ marginLeft: '1rem' }} aria-label='Subsidiaries/Locations Access Level'>
              {subAndLocAccessLevel === AccessLevelEnum.FULL
            ? 'All'
            : subAndLocAccessLevel === AccessLevelEnum.NO_ACCESS
                ? 'None'
                : 'Limited'}{' '}
              Selected
            </Label>
          </Col>
        </Row>
        <EmployerSubLocModal data-testid={'employerSubLocModal'} employerId={props.userInfo.userId} subAndLocAccessLevel={subAndLocAccessLevel} products={props.products} getSubsLocData={getSubsLocData} saveSubsAndLocs={saveSubsAndLocs} subLocs={subLocs}/>
      </Collapse>
    </div>);
};
export default EmployerSubsAndLocs;
